<template>
  <v-dialog
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    max-width="640"
    @keydown.esc="$emit('update:modelValue', false)"
  >
    <v-card>
      <v-card-title class="d-flex align-center ga-2">
        <v-icon icon="mdi-web" />
        <span>{{ isEdit ? 'Редактировать веб-сайт' : 'Добавить веб-сайт' }}</span>
      </v-card-title>

      <v-card-text>
        <v-alert
          v-if="errorMessage"
          type="error"
          variant="tonal"
          density="compact"
          class="mb-3"
          closable
          @click:close="errorMessage = ''"
        >
          {{ errorMessage }}
        </v-alert>

        <v-text-field
          v-model="form.url"
          label="URL сайта"
          placeholder="https://example.com/news"
          variant="outlined"
          density="compact"
          :disabled="busy || isEdit"
          :hint="urlHint"
          persistent-hint
          class="mb-4"
        />

        <v-text-field
          v-model="form.display_name"
          label="Название (необязательно)"
          placeholder="Например: Global Investigative Journalism Network"
          variant="outlined"
          density="compact"
          hint="Если не задать — будет использован URL."
          persistent-hint
          :disabled="busy"
          class="mb-4"
        />

        <!-- Read-only "how it's collected" summary (edit mode) -->
        <v-alert
          v-if="isEdit && discoveryInfo"
          type="info"
          variant="tonal"
          density="compact"
          class="mb-3"
        >
          <div class="text-body-2"><strong>Как собирается</strong></div>
          <div class="text-caption">Метод: {{ discoveryMethodLabel }}</div>
          <div v-if="discoveryInfo.redirected_to" class="text-caption text-warning">
            Адрес перенаправляет на {{ discoveryInfo.redirected_to }}: собирается эта страница, а не раздел
          </div>
          <div v-if="discoveryInfo.feed_url" class="text-caption text-truncate">
            Фид: {{ discoveryInfo.feed_url }}
          </div>
          <div class="text-caption">
            Селектор блока:
            <template v-if="effectiveSelectors.length">
              {{ effectiveSelectors.join(', ') }}
              <span class="text-medium-emphasis">({{ selectorOrigin }})</span>
            </template>
            <template v-else>не используется (сбор по фиду/паттернам)</template>
          </div>
          <div class="text-caption">
            Паттерны статей:
            {{ (form.url_patterns && form.url_patterns.length) ? form.url_patterns.join(', ') : 'нет (берём все ссылки блока/фида)' }}
          </div>
          <div v-if="discoveryInfo.last_extract_status" class="text-caption text-medium-emphasis">
            Последний сбор: {{ discoveryInfo.last_extract_status }}
          </div>
        </v-alert>

        <v-expansion-panels v-model="advancedOpen" flat class="mb-3">
          <v-expansion-panel>
            <v-expansion-panel-title>
              Дополнительно
              <HelpHint section="sources" anchor="sites-advanced" text="Паттерны, входные точки, постраничный обход, метод" class="ml-1" />
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <v-combobox
                v-model="form.url_patterns"
                label="Паттерны URL статей"
                placeholder="Введите паттерн и нажмите Enter (/news/**)"
                hint="Можно добавить несколько. Ссылка пройдёт фильтр, если совпадает хотя бы с одним паттерном."
                persistent-hint
                append-inner-icon="mdi-help-circle-outline"
                @click:append-inner="patternHelp = !patternHelp"
                multiple
                chips
                closable-chips
                clearable
                hide-no-data
                variant="outlined"
                density="compact"
                :disabled="busy"
                class="mb-4"
              />
              <v-expand-transition>
                <v-alert v-if="patternHelp" type="info" variant="tonal" density="compact" class="mb-4">
                  <div class="text-body-2 font-weight-medium mb-1">Синтаксис паттернов (по пути URL)</div>
                  <div class="text-caption">
                    <code>*</code> — ровно <strong>один</strong> сегмент пути (без «/»)<br>
                    <code>**</code> — <strong>любое число</strong> сегментов (включая «/»)<br>
                    <code>?</code> — один символ (не «/»)
                  </div>
                  <v-divider class="my-2" />
                  <div class="text-caption">
                    <code>/news/**</code> — все статьи раздела news, любая вложенность<br>
                    <code>/2026/**</code> — всё, что начинается с /2026/ (напр. /2026/09/02/заголовок)<br>
                    <code>/2026/*</code> — ⚠ только /2026/&lt;один-сегмент&gt; — вложенные статьи НЕ пройдут<br>
                    <code>/post/**</code> — статьи вида /post/заголовок<br>
                    <code>/*/article-?.html</code> — ? заменяет один символ
                  </div>
                </v-alert>
              </v-expand-transition>
              <v-combobox
                v-model="form.entry_points"
                label="Входные точки"
                placeholder="URL раздела и Enter (https://site.com/topic/a)"
                hint="Страницы, которые сканируем на ссылки статей. По умолчанию — сам сайт. Добавьте разделы, если статьи не видны с главной."
                persistent-hint
                multiple
                chips
                closable-chips
                clearable
                hide-no-data
                variant="outlined"
                density="compact"
                :disabled="busy"
                class="mb-4"
              />
              <v-select
                v-model="form.pagination_mode"
                :items="paginationOptions"
                label="Постраничный обход"
                variant="outlined"
                density="compact"
                hint="История собирается на первом прогоне (до лимита страниц); дальше обход останавливается на первой странице без новых ссылок."
                persistent-hint
                :disabled="busy"
                class="mb-4"
              />
              <v-text-field
                v-if="form.pagination_mode === 'template'"
                v-model="form.pagination_template"
                label="Шаблон URL страницы"
                placeholder="https://site.com/news?page={n}"
                variant="outlined"
                density="compact"
                hint="{n} — номер страницы. Примеры: ?page={n} · /page/{n} · ?p={n}"
                persistent-hint
                :disabled="busy"
                class="mb-4"
              />
              <v-row v-if="form.pagination_mode" dense class="mb-2">
                <v-col cols="6" v-if="form.pagination_mode === 'template'">
                  <v-text-field
                    v-model.number="form.pagination_start"
                    label="Начинать с n ="
                    type="number" min="0" max="100"
                    variant="outlined" density="compact"
                    hint="Первая страница — сам URL источника; шаблон продолжает с этого n."
                    persistent-hint
                    :disabled="busy"
                  />
                </v-col>
                <v-col :cols="form.pagination_mode === 'template' ? 6 : 12">
                  <v-text-field
                    v-model.number="form.pagination_max"
                    label="Макс. страниц за цикл"
                    type="number" min="1" max="100"
                    variant="outlined" density="compact"
                    hint="Защита от бесконечных архивов."
                    persistent-hint
                    :disabled="busy"
                  />
                </v-col>
              </v-row>
              <v-select
                v-model="form.pinned_method"
                :items="pinnedMethodOptions"
                label="Метод получения ссылок"
                variant="outlined"
                density="compact"
                hint="Авто — система сама подбирает лучший метод. Фиксация метода отключает автоподбор."
                persistent-hint
                :disabled="busy"
                class="mb-4"
              />
              <v-text-field
                v-if="form.pinned_method === 'selector'"
                v-model="form.custom_selector"
                label="CSS-селектор блока"
                placeholder=".news-list a"
                variant="outlined"
                density="compact"
                hint="Контейнер ленты со ссылками на статьи. Обязателен для метода «CSS-селектор»."
                persistent-hint
                :disabled="busy"
                class="mb-4"
              />
              <!-- Optional manual mode: run discovery now and review/exclude
                   the found blocks before saving, instead of the default
                   background check. -->
              <v-btn
                v-if="!isEdit && !result"
                variant="tonal"
                color="primary"
                size="small"
                block
                @click="startPreview"
                :loading="busy"
                :disabled="!canPreview"
              >
                Проверить и выбрать блоки
              </v-btn>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>

        <!-- Progress -->
        <div v-if="busy" class="my-4">
          <v-progress-linear indeterminate color="primary" class="mb-2" />
          <div class="text-caption text-medium-emphasis">
            {{ stepLabel }}
          </div>
        </div>

        <!-- Preview result -->
        <div v-if="result" class="mt-2">
          <v-divider class="mb-3" />
          <div class="d-flex align-center mb-3 ga-2">
            <v-chip size="small" color="success" variant="tonal">
              {{ methodLabel(result.method) }}
            </v-chip>
            <span v-if="result.feed_url" class="text-caption text-medium-emphasis text-truncate">
              {{ result.feed_url }}
            </span>
          </div>
          <v-alert v-if="result.redirected_to" type="warning" variant="tonal" density="compact" class="mb-3">
            Адрес раздела перенаправляет на <strong>{{ result.redirected_to }}</strong>. Ссылки взяты
            с этой страницы, а не с раздела. Проверьте адрес: например, у русской версии сайта
            бывает свой домен.
          </v-alert>

          <div
            v-for="(block, bi) in groupedBlocks"
            :key="bi"
            class="mb-3"
          >
            <div class="d-flex align-center justify-space-between mb-1">
              <div>
                <strong>
                  Блок #{{ bi + 1 }}
                </strong>
                <span v-if="block.matched_pattern" class="text-caption text-medium-emphasis ml-2">
                  паттерн «{{ block.matched_pattern }}»
                </span>
                <span v-else-if="block.selector" class="text-caption text-medium-emphasis ml-2">
                  селектор «{{ block.selector }}»
                </span>
                <v-chip size="x-small" class="ml-2">{{ block.urls.length }}</v-chip>
              </div>
              <v-btn
                v-if="groupedBlocks.length > 1"
                size="small"
                variant="text"
                color="warning"
                @click="excludeBlock(bi)"
              >
                Исключить
              </v-btn>
            </div>
            <v-list density="compact" class="bg-grey-lighten-5 py-1">
              <v-list-item
                v-for="item in sampleForBlock(block).slice(0, 5)"
                :key="item.url"
                :title="item.title || item.url"
                :subtitle="item.title ? item.url : null"
                :href="item.url"
                target="_blank"
                rel="noopener"
                class="text-caption"
              />
            </v-list>
          </div>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="close" :disabled="busy">Отмена</v-btn>
        <!-- Edit mode: patterns/name are saved directly, no re-discovery. -->
        <v-btn
          v-if="isEdit"
          color="primary"
          variant="elevated"
          @click="updateSource"
          :loading="saving"
        >
          Сохранить
        </v-btn>
        <template v-else>
          <!-- After an interactive preview: save the reviewed blocks. -->
          <v-btn
            v-if="result"
            color="primary"
            variant="elevated"
            @click="saveSource"
            :loading="saving"
            :disabled="!hasAnyLinks"
          >
            Сохранить выбранные
          </v-btn>
          <!-- Default: save immediately, discovery runs in the background. -->
          <v-btn
            v-else
            color="primary"
            variant="elevated"
            @click="saveBackground"
            :loading="saving || busy"
            :disabled="!canPreview"
          >
            Добавить
          </v-btn>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex';
import apiClient from '@/apiClient';

const POLL_INTERVAL_MS = 2500;
// 6-minute ceiling. CF-protected sites with multi-pattern discovery
// can run a single preview through several FlareSolverr round-trips.
const MAX_POLLS = 144;

export default {
  name: 'AddWebSourceDialog',
  props: {
    modelValue: { type: Boolean, required: true },
    // When set, the dialog opens in edit mode for this user source.
    editSource: { type: Object, default: null },
  },
  emits: ['update:modelValue', 'created', 'updated'],
  data() {
    return {
      advancedOpen: undefined,
      patternHelp: false,
      form: this.makeEmptyForm(),
      taskId: null,
      pollTimer: null,
      pollCount: 0,
      busy: false,
      saving: false,
      step: null,
      result: null,
      excluded: new Set(),
      errorMessage: '',
    };
  },
  computed: {
    ...mapGetters(['hasPermission']),
    canOnion() { return this.hasPermission('feature:onion_sources'); },
    isOnionUrl() {
      const u = (this.form.url || '').trim().toLowerCase();
      return /(^|\/\/)[a-z0-9.-]*\.onion(\/|$)/.test(u);
    },
    urlHint() {
      if (this.isEdit) return 'URL источника изменить нельзя — удалите и добавьте заново.';
      if (this.isOnionUrl) {
        return this.canOnion
          ? 'Onion-сайт (Tor): проверка и сбор идут через Tor, только текст без медиа, ожидание до 1–2 минут.'
          : 'Onion-адреса недоступны: нет права feature:onion_sources — обратитесь к администратору.';
      }
      return this.canOnion
        ? 'Можно без https:// — подставим автоматически. Поддерживаются .onion (Tor, только v3).'
        : 'Можно без https:// — подставим автоматически.';
    },
    paginationOptions() {
      return [
        { value: '', title: 'Выключен' },
        { value: 'auto', title: 'Авто (rel=next / ?page=N+1)' },
        { value: 'template', title: 'Шаблон URL ({n})' },
      ];
    },
    paginationBody() {
      if (!this.form.pagination_mode) return null;
      const p = { mode: this.form.pagination_mode, max_pages: this.form.pagination_max || 20 };
      if (p.mode === 'template') {
        p.template = (this.form.pagination_template || '').trim();
        p.start = this.form.pagination_start ?? 2;
      }
      return p;
    },
    pinnedMethodOptions() {
      return [
        { value: '', title: 'Авто (рекомендуется)' },
        { value: 'rss', title: 'RSS / Atom' },
        { value: 'sitemap', title: 'Sitemap' },
        { value: 'selector', title: 'CSS-селектор (задайте селектор ниже)' },
        { value: 'llm', title: 'LLM' },
      ];
    },
    isEdit() {
      return !!this.editSource;
    },
    discoveryInfo() {
      return this.editSource?.discovery_info || null;
    },
    discoveryMethodLabel() {
      const m = this.discoveryInfo?.method;
      return ({
        rss: 'RSS-фид',
        sitemap: 'Sitemap',
        jsonld: 'JSON-LD',
        heuristic: 'Автоподбор блока (эвристика)',
        user_pattern: 'По вашим паттернам',
        selector: 'CSS-селектор',
        llm: 'AI-подбор',
      })[m] || m || 'не определён';
    },
    effectiveSelectors() {
      // User override (UserSource) wins over the site-side selector.
      const user = this.editSource?.extra_attributes?.block_selectors;
      if (Array.isArray(user) && user.length) return user;
      const site = this.discoveryInfo?.block_selectors;
      return Array.isArray(site) ? site.filter(Boolean) : [];
    },
    selectorOrigin() {
      const user = this.editSource?.extra_attributes?.block_selectors;
      return (Array.isArray(user) && user.length) ? 'ваш' : 'сайтный';
    },
    normalizedUrl() {
      // Let the user type a bare domain — prepend https:// for them.
      const u = (this.form.url || '').trim();
      if (!u) return '';
      return /^https?:\/\//i.test(u) ? u : `https://${u}`;
    },
    canPreview() {
      if (!this.normalizedUrl) return false;
      try {
        return /\./.test(new URL(this.normalizedUrl).hostname);
      } catch {
        return false;
      }
    },
    stepLabel() {
      const map = {
        starting: 'Запускаем проверку…',
        using_selectors: 'Применяем заданный селектор…',
        discovering_rss: 'Ищем RSS / Atom…',
        trying_sitemap: 'Проверяем sitemap…',
        scanning_jsonld: 'Сканируем JSON-LD…',
        analysing_structure: 'Анализируем структуру страницы…',
        matching_user_patterns: 'Применяем ваши паттерны…',
        asking_llm: 'Просим AI помочь…',
        multi_pattern_discovery: 'Обрабатываем несколько разделов сайта — это занимает 1-3 минуты…',
      };
      return map[this.step] || 'Обработка…';
    },
    groupedBlocks() {
      if (!this.result?.blocks) return [];
      return this.result.blocks.filter((_, i) => !this.excluded.has(i));
    },
    hasAnyLinks() {
      return this.groupedBlocks.some(b => (b.urls || []).length > 0);
    },
  },
  watch: {
    modelValue(v) {
      if (v) {
        this.reset();
      } else {
        this.stopPolling();
      }
    },
  },
  methods: {
    makeEmptyForm() {
      return {
        url: '',
        display_name: '',
        url_patterns: [],
        entry_points: [],
        custom_selector: '',
        pinned_method: '',
        pagination_mode: '',
        pagination_template: '',
        pagination_start: 2,
        pagination_max: 20,
      };
    },
    reset() {
      this.form = this.makeEmptyForm();
      this.result = null;
      this.taskId = null;
      this.busy = false;
      this.saving = false;
      this.step = null;
      this.pollCount = 0;
      this.excluded = new Set();
      this.errorMessage = '';
      this.advancedOpen = undefined;
      this.stopPolling();

      if (this.editSource) {
        const ex = this.editSource.extra_attributes || {};
        this.form.url = this.editSource.identifier || '';
        this.form.display_name = this.editSource.display_name || '';
        this.form.url_patterns = Array.isArray(ex.url_patterns) ? [...ex.url_patterns] : [];
        this.form.entry_points = Array.isArray(ex.entry_points) ? [...ex.entry_points] : [];
        this.form.custom_selector = (ex.block_selectors && ex.block_selectors[0]) || '';
        // Старые источники: селектор без пина = пин 'selector' (старая семантика).
        this.form.pinned_method = ex.pinned_method || (this.form.custom_selector ? 'selector' : '');
        const pg = ex.pagination || {};
        this.form.pagination_mode = pg.mode || '';
        this.form.pagination_template = pg.template || '';
        this.form.pagination_start = pg.start ?? 2;
        this.form.pagination_max = pg.max_pages ?? 20;
        // Show the patterns/selector straight away in edit mode.
        this.advancedOpen = 0;
      }
    },
    close() {
      this.stopPolling();
      this.$emit('update:modelValue', false);
    },
    methodLabel(m) {
      return ({
        rss: 'RSS',
        sitemap: 'Sitemap',
        jsonld: 'JSON-LD',
        heuristic: 'Эвристика',
        user_pattern: 'По вашим паттернам',
        llm: 'AI',
        selector: 'CSS-селектор',
      })[m] || m || '';
    },
    sampleForBlock(block) {
      if (!this.result?.sample) return (block.urls || []).map(u => ({ title: '', url: u }));
      const urls = new Set(block.urls || []);
      const fromSample = this.result.sample.filter(s => urls.has(s.url));
      if (fromSample.length) return fromSample;
      return (block.urls || []).map(u => ({ title: '', url: u }));
    },
    async startPreview() {
      this.errorMessage = '';
      this.result = null;
      this.excluded = new Set();
      this.busy = true;
      this.step = 'starting';
      try {
        const body = {
          url: this.normalizedUrl,
          url_patterns: this.form.url_patterns || [],
          entry_points: this.form.entry_points || [],
          block_selectors: this.form.custom_selector
            ? [this.form.custom_selector.trim()]
            : [],
        };
        const r = await apiClient.post('/user-sources/web/preview', body);
        this.taskId = r.data.task_id;
        this.pollCount = 0;
        this.pollTimer = setInterval(this.pollPreview, POLL_INTERVAL_MS);
      } catch (e) {
        this.errorMessage = this.extractError(e) || 'Не удалось запустить проверку';
        this.busy = false;
      }
    },
    async pollPreview() {
      this.pollCount += 1;
      if (this.pollCount > MAX_POLLS) {
        this.stopPolling();
        this.busy = false;
        this.errorMessage = 'Время ожидания истекло';
        return;
      }
      try {
        const r = await apiClient.get(`/user-sources/web/preview/${this.taskId}`);
        const s = r.data;
        this.step = s.step || this.step;
        if (s.status === 'ready') {
          this.stopPolling();
          this.busy = false;
          this.result = s;
        } else if (s.status === 'failed') {
          this.stopPolling();
          this.busy = false;
          this.errorMessage = this.failureMessage(s.error);
        }
      } catch (e) {
        // 404 — task expired; otherwise just keep polling.
        if (e?.response?.status === 404) {
          this.stopPolling();
          this.busy = false;
          this.errorMessage = 'Задача не найдена';
        }
      }
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    failureMessage(code) {
      return ({
        no_links_found: 'Не удалось найти блок со ссылками на публикации. Попробуйте указать CSS-селектор вручную.',
        no_proxy_available: 'Нет свободных прокси. Попробуйте позже.',
        cf_blocked: 'Сайт защищён Cloudflare и блокирует наши прокси. Подождите 10-15 минут или добавьте больше residential-прокси из других подсетей в /admin/proxies.',
      })[code] || `Ошибка: ${code || 'unknown'}`;
    },
    excludeBlock(idx) {
      this.excluded = new Set([...this.excluded, idx]);
    },
    async saveSource() {
      if (this.form.pagination_mode === 'template' && !(this.form.pagination_template || '').includes('{n}')) {
        this.errorMessage = 'Шаблон страницы должен содержать {n} (номер страницы).';
        return;
      }
      if (this.form.pinned_method === 'selector' && !(this.form.custom_selector || '').trim()) {
        this.errorMessage = 'Для метода «CSS-селектор» укажите селектор блока.';
        return;
      }
      this.saving = true;
      this.errorMessage = '';
      try {
        const keptBlocks = this.groupedBlocks;
        const allSelectors = keptBlocks
          .map(b => b.selector)
          .filter(Boolean);
        // If the user filled the custom selector and it survived, prefer it
        // verbatim; otherwise use what the cascade returned.
        const finalSelectors = this.form.pinned_method === 'selector' && this.form.custom_selector
          ? [this.form.custom_selector.trim()]
          : allSelectors;
        const matchedPatterns = keptBlocks
          .map(b => b.matched_pattern)
          .filter(Boolean);
        const finalPatterns = this.form.url_patterns?.length
          ? this.form.url_patterns
          : matchedPatterns;

        const body = {
          url: this.normalizedUrl,
          display_name: (this.form.display_name || '').trim() || null,
          background: false,
          discovery_method: this.result.method,
          feed_url: this.result.feed_url || null,
          block_selectors: finalSelectors,
          url_patterns: finalPatterns,
          entry_points: this.form.entry_points || [],
          pinned_method: this.form.pinned_method || null,
          pagination: this.paginationBody,
          // requires_js is self-learned by the worker — not user-set
          tags: [],
        };
        const r = await apiClient.post('/user-sources/web', body);
        this.$emit('created', r.data);
        this.close();
      } catch (e) {
        this.errorMessage = this.extractError(e) || 'Не удалось сохранить источник';
      } finally {
        this.saving = false;
      }
    },
    async saveBackground() {
      if (this.form.pagination_mode === 'template' && !(this.form.pagination_template || '').includes('{n}')) {
        this.errorMessage = 'Шаблон страницы должен содержать {n} (номер страницы).';
        return;
      }
      if (this.form.pinned_method === 'selector' && !(this.form.custom_selector || '').trim()) {
        this.errorMessage = 'Для метода «CSS-селектор» укажите селектор блока.';
        return;
      }
      this.saving = true;
      this.errorMessage = '';
      try {
        const body = {
          url: this.normalizedUrl,
          display_name: (this.form.display_name || '').trim() || null,
          background: true,
          url_patterns: this.form.url_patterns || [],
          entry_points: this.form.entry_points || [],
          block_selectors: this.form.pinned_method === 'selector' && this.form.custom_selector
            ? [this.form.custom_selector.trim()]
            : [],
          pinned_method: this.form.pinned_method || null,
          pagination: this.paginationBody,
          tags: [],
        };
        const r = await apiClient.post('/user-sources/web', body);
        this.$emit('created', r.data);
        this.close();
      } catch (e) {
        // 409 (duplicate) and other errors surface here.
        this.errorMessage = this.extractError(e) || 'Не удалось добавить источник';
      } finally {
        this.saving = false;
      }
    },
    async updateSource() {
      if (this.form.pagination_mode === 'template' && !(this.form.pagination_template || '').includes('{n}')) {
        this.errorMessage = 'Шаблон страницы должен содержать {n} (номер страницы).';
        return;
      }
      if (this.form.pinned_method === 'selector' && !(this.form.custom_selector || '').trim()) {
        this.errorMessage = 'Для метода «CSS-селектор» укажите селектор блока.';
        return;
      }
      this.saving = true;
      this.errorMessage = '';
      try {
        const body = {
          display_name: (this.form.display_name || '').trim() || null,
          url_patterns: this.form.url_patterns || [],
          entry_points: this.form.entry_points || [],
          block_selectors: this.form.pinned_method === 'selector' && this.form.custom_selector
            ? [this.form.custom_selector.trim()]
            : [],
          // '' resets the pin back to auto-cascade
          pinned_method: this.form.pinned_method || '',
          pagination: this.paginationBody || { mode: 'off' },
        };
        const r = await apiClient.put(`/user-sources/${this.editSource.id}`, body);
        this.$emit('updated', r.data);
        this.close();
      } catch (e) {
        this.errorMessage = this.extractError(e) || 'Не удалось сохранить изменения';
      } finally {
        this.saving = false;
      }
    },
    extractError(e) {
      return e?.response?.data?.detail
        || e?.response?.data?.message
        || e?.message;
    },
  },
  beforeUnmount() {
    this.stopPolling();
  },
};
</script>
